/**
 * Every difference between two revisions, filterable, as a table or as cards.
 *
 * The technical record changes sit under a disclosure of their own: they are
 * kept for audit and say nothing about the curriculum, so they must not
 * outnumber the academic changes above them.
 *
 * @module components/portfolio/comparison/ChangeRegister
 */

import React from "react";

import { ChangeValues, ReadableValue } from "@/components/portfolio/comparison/change-values";
import {
  COMPARISON_AREA_LABELS,
  type ComparisonArea,
  matchesComparisonArea,
} from "@/components/portfolio/comparison/comparison-areas";
import type { AcademicChange } from "@/components/portfolio/comparison/diff-types";
import { changeKindTone } from "@/components/portfolio/comparison/module-status";
import { humanizeIdentifier, revisionCategoryLabel } from "@/components/shared/diff-labels";
import {
  Accordion,
  AccordionItem,
  Badge,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  EntityCard,
  FILTER_CONTROL_ROW,
  FormField,
  Heading,
  MutedText,
  SelectField,
  Text,
  TextField,
} from "@/components/ui";

export interface ChangeRegisterProps {
  changes: readonly AcademicChange[];
  /** System metadata differences, kept for audit and shown apart. */
  technicalChanges: readonly AcademicChange[];
  category: ComparisonArea;
  onCategoryChange: (category: ComparisonArea) => void;
  /**
   * Held by the screen, as the area is: the register unmounts with its tab,
   * so a search kept here would reset on a tab round-trip while the area
   * survived, leaving the reader half their filters back.
   */
  search: string;
  onSearchChange: (search: string) => void;
}

/** One change, as a row in a divided list or as a card in a grid. */
function ChangeEntry({
  change,
  variant = "row",
}: {
  change: AcademicChange;
  variant?: "row" | "card";
}): React.ReactElement {
  if (variant === "card") {
    return (
      <EntityCard
        className="h-full"
        eyebrow={
          <Badge size="sm" tone={changeKindTone(change.kind)}>
            {humanizeIdentifier(change.kind)}
          </Badge>
        }
        title={change.item}
        meta={change.itemContext || undefined}
      >
        <Text className="text-base-content">{change.description}</Text>
        <ChangeValues change={change} />
      </EntityCard>
    );
  }
  return (
    <div className="py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge size="sm" tone={changeKindTone(change.kind)}>
          {humanizeIdentifier(change.kind)}
        </Badge>
        <span className="font-medium">{change.item}</span>
        {change.itemContext ? (
          <MutedText size="caption" as="span">
            {change.itemContext}
          </MutedText>
        ) : null}
      </div>
      <Text className="mt-2">{change.description}</Text>
      <ChangeValues change={change} />
    </div>
  );
}

const COLUMNS: readonly Column<AcademicChange>[] = [
  {
    key: "item",
    header: "Item",
    width: "min-w-44",
    render: (change) => (
      <>
        <div className="font-medium">{change.item}</div>
        {change.itemContext ? (
          <MutedText size="caption" className="mt-1">
            {change.itemContext}
          </MutedText>
        ) : null}
      </>
    ),
  },
  {
    key: "area",
    header: "Area",
    priority: 3,
    render: (change) => revisionCategoryLabel(change.category),
  },
  {
    key: "change",
    header: "Change",
    width: "min-w-64",
    render: (change) => (
      <>
        <Badge size="sm" tone={changeKindTone(change.kind)}>
          {humanizeIdentifier(change.kind)}
        </Badge>
        <Text className="mt-2">{change.description}</Text>
      </>
    ),
  },
  {
    key: "before",
    header: "Earlier value",
    width: "min-w-40",
    render: (change) => <ReadableValue value={change.before} />,
  },
  {
    key: "after",
    header: "Later value",
    width: "min-w-40",
    render: (change) => <ReadableValue value={change.after} />,
  },
];

/**
 * @example
 * ```tsx
 * <ChangeRegister changes={academic} technicalChanges={technical} category={area} onCategoryChange={setArea} />
 * ```
 */
export function ChangeRegister({
  changes,
  technicalChanges,
  category,
  onCategoryChange,
  search,
  onSearchChange,
}: ChangeRegisterProps): React.ReactElement {
  const normalisedSearch = search.trim().toLocaleLowerCase("en-IE");
  const offered = (Object.keys(COMPARISON_AREA_LABELS) as ComparisonArea[]).filter(
    (area) => area !== "all" && changes.some((change) => matchesComparisonArea(change, area)),
  );
  // Derived, not corrected: the area outlives a change of revision, and a new
  // pair may not have the one chosen. A controlled select whose value names no
  // option renders blank while the panel insists a filter is on.
  const area = category === "all" || offered.includes(category) ? category : "all";
  const visible = changes.filter(
    (change) =>
      matchesComparisonArea(change, area) &&
      (normalisedSearch.length === 0 ||
        [change.item, change.itemContext, change.field, change.description].some((value) =>
          value?.toLocaleLowerCase("en-IE").includes(normalisedSearch),
        )),
  );

  // `CollectionView` draws one rendering and shows this in place of it, so
  // there is one message however the list is drawn.
  const noMatches = (
    <EmptyState
      icon="funnel"
      title="No curriculum changes match these filters"
      description="Clear a filter to see the rest of the register."
      density="row"
    />
  );

  return (
    <section aria-labelledby="all-changes-heading">
      <Heading level={2} id="all-changes-heading">
        Change register
      </Heading>
      <MutedText className="mt-1">
        Filter and browse every academic, structural, assessment, mapping, and administrative
        difference.
      </MutedText>
      {/* Gated on the academic changes alone. A re-approval that only bumps a
          timestamp leaves none of them, and a filter panel over an empty list
          tells the reader to clear a filter that is not set, through a clear
          control it then refuses to operate. */}
      {changes.length === 0 ? (
        <EmptyState
          className="mt-6"
          icon="equals"
          title="These revisions contain the same programme content"
          description={
            technicalChanges.length > 0 ? "Only the technical record differs." : undefined
          }
          density="row"
          data-testid="comparison-changes-empty"
        />
      ) : (
        <div className="mt-5">
          <CollectionView
            storageKey="comparison-change-register"
            columns={COLUMNS}
            rows={visible}
            rowKey={(change) => `${change.path}:${change.kind}`}
            caption="Changes between the two revisions"
            density="sm"
            // Every cell but the change itself is a word or a number, and a
            // row runs to three lines, so centring leaves nothing to read
            // across.
            verticalAlign="top"
            cards={{
              render: (change) => <ChangeEntry change={change} variant="card" />,
              "data-testid": "comparison-change-register-cards",
            }}
            filtering={{
              label: "Filter changes",
              count: `${visible.length} of ${countLabel(changes.length, "change")} shown`,
              // From the controls, not from what they match, as
              // `useCollectionFilters` is: a search of only spaces narrows
              // nothing and still fills the field, so a Clear gated on the
              // trimmed value leaves nothing to press.
              active: search.length > 0 || area !== "all",
              defaultOpen: true,
              onClear: () => {
                onSearchChange("");
                onCategoryChange("all");
              },
              controls: (
                <div className={FILTER_CONTROL_ROW}>
                  <FormField label="Find a field" htmlFor="field-comparison-search">
                    <TextField
                      id="field-comparison-search"
                      value={search}
                      onChange={onSearchChange}
                      placeholder="Module title, assessment, credits"
                    />
                  </FormField>
                  <FormField label="Area" htmlFor="field-comparison-category">
                    <SelectField
                      id="field-comparison-category"
                      value={area}
                      onChange={(value) => onCategoryChange(value as ComparisonArea)}
                      options={[
                        { value: "all", label: "All curriculum areas" },
                        ...offered.map((value) => ({
                          value,
                          label: COMPARISON_AREA_LABELS[value],
                        })),
                      ]}
                    />
                  </FormField>
                </div>
              ),
            }}
            emptyState={noMatches}
            data-testid="comparison-change-register"
          />
        </div>
      )}
      {technicalChanges.length > 0 ? (
        <Accordion id="comparison-technical-changes" className="mt-6">
          <AccordionItem
            eventKey="comparison-technical-changes"
            headingLevel={3}
            title="Technical record changes"
            subtitle={`${technicalChanges.length} system metadata ${technicalChanges.length === 1 ? "difference" : "differences"}, retained for audit`}
            data-testid="comparison-technical-changes"
          >
            <div className="divide-y divide-base-300">
              {technicalChanges.map((change) => (
                <ChangeEntry key={`${change.path}:${change.kind}`} change={change} />
              ))}
            </div>
          </AccordionItem>
        </Accordion>
      ) : null}
    </section>
  );
}
